type FetchJsonRetryOptions = {
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  retryUnsafeMethods?: boolean;
};

const retryableStatusCodes = new Set([
  408,
  425,
  429,
  500,
  502,
  503,
  504,
]);

const safeRetryMethods = new Set([
  'GET',
  'HEAD',
  'OPTIONS',
]);

const wait = (ms: number) =>
  new Promise((resolve) =>
    globalThis.setTimeout(resolve, ms)
  );

const createRequestId = () => {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }
};

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

export async function fetchWithRetry(
  input: FetchInput,
  init: FetchInit = {},
  options: FetchJsonRetryOptions = {}
) {
  const {
    timeoutMs = 10000,
    retries: requestedRetries = 1,
    retryDelayMs = 250,
    retryUnsafeMethods = false,
  } = options;

  const method = String(
    init.method ?? 'GET'
  ).toUpperCase();

  const retries =
    safeRetryMethods.has(method) ||
    retryUnsafeMethods
      ? Math.max(0, requestedRetries)
      : 0;

  const requestId = createRequestId();
  const headers = new Headers(init.headers);

  if (!headers.has('X-Request-ID')) {
    headers.set('X-Request-ID', requestId);
  }

  let lastError: unknown = null;

  for (
    let attempt = 0;
    attempt <= retries;
    attempt += 1
  ) {
    const controller = new AbortController();

    const timeoutId = globalThis.setTimeout(
      () => controller.abort(),
      timeoutMs
    );

    try {
      const response = await fetch(input, {
        ...init,
        headers,
        signal: controller.signal,
      });

      if (
        response.ok ||
        !retryableStatusCodes.has(
          response.status
        ) ||
        attempt === retries
      ) {
        return response;
      }

      lastError = new Error(
        `HTTP ${response.status}`
      );
    } catch (error) {
      lastError = error;

      if (attempt === retries) {
        throw error;
      }
    } finally {
      globalThis.clearTimeout(timeoutId);
    }

    if (attempt < retries) {
      await wait(retryDelayMs);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error(
        'Não foi possível carregar a API.'
      );
}
